import {useDirtyGuard} from './hooks/useUnsavedChanges';
import {PageHeader,Tabs,Skeleton,EmptyState,Button} from './ui';
import React, { useEffect, useState } from 'react';
import { API_BASE, apiFetch } from './api';
import { mergeStyles, theme } from './theme';

const styles = {
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) 320px', gap: '18px', alignItems: 'start', width: '100%', minWidth: 0 },
  stack: { display: 'grid', gap: '18px', width: '100%', minWidth: 0 },
  card: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)', width: '100%', minWidth: 0 },
  title: { margin: 0, color: 'var(--board-text-strong, #fff)', fontSize: '22px', fontWeight: 800 },
  subtitle: { margin: '8px 0 0', color: 'var(--board-text-muted, rgba(255,255,255,0.60))', fontSize: '13px', lineHeight: 1.5 },
  section: { display: 'grid', gap: '18px', paddingTop: '22px', borderTop: '1px solid var(--board-border, rgba(148,163,184,0.12))' },
  sectionHeader: { display: 'grid', gap: '4px' },
  sectionTitle: { margin: 0, color: 'var(--board-text-strong, #fff)', fontSize: '16px', fontWeight: 800 },
  sectionText: { margin: 0, color: 'var(--board-text-muted, rgba(255,255,255,0.56))', fontSize: '12px', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', width: '100%', minWidth: 0 },
  field: { display: 'grid', gap: '8px', alignContent: 'start' },
  label: { display: 'block', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  input: { ...theme.input, width: '100%' },
  colorInput: { ...theme.input, width: '100%', minHeight: '44px', padding: '4px 6px', cursor: 'pointer' },
  domainInputRow: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 44px', gap: '10px', alignItems: 'stretch' },
  domainSaveButton: { ...theme.button, ...theme.successButton, width: '44px', height: '44px', padding: 0, display: 'grid', placeItems: 'center', fontSize: '14px', fontWeight: 800 },
  buttonRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' },
  button: { ...theme.button, ...theme.primaryButton },
  mutedButton: { ...theme.button, ...theme.mutedButton },
  hint: { color: 'var(--board-text-muted, rgba(255,255,255,0.58))', fontSize: '12px', margin: 0, lineHeight: 1.5 },
  statusSuccess: { color: '#86efac', fontSize: '12px', margin: 0, lineHeight: 1.5 },
  statusError: { color: '#fca5a5', fontSize: '12px', margin: 0, lineHeight: 1.5 },
  sideCardTitle: { margin: 0, color: 'var(--board-text-strong, #fff)', fontSize: '16px', fontWeight: 800 },
  sideCardText: { margin: '6px 0 0', color: 'var(--board-text-muted, rgba(255,255,255,0.58))', fontSize: '12px', lineHeight: 1.5 },
  previewFrame: { width: '100%', minHeight: '160px', borderRadius: '6px', border: '1px solid var(--board-border, rgba(148,163,184,0.14))', background: 'var(--board-surface-soft, rgba(255,255,255,0.03))', display: 'grid', placeItems: 'center', overflow: 'hidden' },
  previewImage: { maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', padding: '20px' },
  previewFallback: { width: '92px', height: '92px', borderRadius: '6px', background: 'var(--board-accent-gradient-soft, linear-gradient(135deg, rgba(59,130,246,0.24) 0%, rgba(99,102,241,0.24) 100%))', border: '1px solid var(--board-accent-border, rgba(96,165,250,0.18))', display: 'grid', placeItems: 'center', color: 'var(--board-text-strong, #fff)', fontWeight: 800, fontSize: '26px' },
  licenseBox: { display: 'grid', gap: '8px', padding: '14px 16px', borderRadius: '6px', background: 'var(--board-surface-soft, rgba(255,255,255,0.03))', border: '1px solid var(--board-border, rgba(148,163,184,0.12))' },
  licenseValue: { fontSize: '20px', fontWeight: 800, color: 'var(--board-text-strong, #fff)' },
  colorRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  colorSwatch: { width: '44px', height: '44px', borderRadius: '6px', border: '1px solid var(--board-border, rgba(148,163,184,0.18))', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' },
  domainRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '12px 14px', borderRadius: '6px', background: 'var(--board-surface-soft, rgba(255,255,255,0.03))', border: '1px solid var(--board-border, rgba(148,163,184,0.12))' },
  domainStatus: { ...theme.chip, padding: '5px 9px', borderRadius: '999px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(74,222,128,0.24)', fontSize: '11px' },
};

function GeneralSettings() {
  const [loaded,setLoaded]=useState(false),[loadError,setLoadError]=useState(''),[reload,setReload]=useState(0);
  const [dirty,setDirty]=useState(false);useDirtyGuard(dirty);
  const [section,setSection]=useState('branding'),[saveError,setSaveError]=useState(''),[saving,setSaving]=useState(false);
  const [settings, setSettings] = useState({
    board_name: '',
    site_logo_url: '',
    api_key: '',
    eventworx_base_url: '',
    eventworx_api_token: '',
    eventworx_api_token_configured: false,
    custom_domain: '',
    custom_domain_target: '',
    theme_accent_1: '#3b82f6',
    theme_accent_2: '#6366f1',
    default_theme_accent_1: '#3b82f6',
    default_theme_accent_2: '#6366f1',
    using_default_theme_colors: true,
    license_preset_name: 'CUSTOM',
    api_key_configured: false,
    features: { customLogo: true, rentmanApi: true, customDomain: false, customColors: false },
  });
  const [saved, setSaved] = useState(false);
  const [apiKeyCheck, setApiKeyCheck] = useState({ status: 'idle', message: '' });
  const [proxySyncMessage, setProxySyncMessage] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [apiKeyMasked, setApiKeyMasked] = useState(false);
  const [eventworxTokenMasked, setEventworxTokenMasked] = useState(false);
  const customDomainTarget = settings.custom_domain_target || window.location.hostname;

  useEffect(() => {
    setLoaded(false);setLoadError('');
    apiFetch(`${API_BASE}/settings`)
      .then((response) => {if(!response.ok)throw new Error('Einstellungen konnten nicht geladen werden.');return response.json()})
      .then((data) => {
        setSettings({
          board_name: data.board_name || '',
          site_logo_url: data.site_logo_url || '',
          api_key: data.api_key || '',
          api_key_configured: Boolean(data.api_key_configured),
          eventworx_base_url: data.eventworx_base_url || '',
          eventworx_api_token: data.eventworx_api_token || '',
          eventworx_api_token_configured: Boolean(data.eventworx_api_token_configured),
          custom_domain: data.custom_domain || '',
          custom_domain_target: data.custom_domain_target || '',
          theme_accent_1: data.theme_accent_1 || '#3b82f6',
          theme_accent_2: data.theme_accent_2 || '#6366f1',
          default_theme_accent_1: data.default_theme_accent_1 || '#3b82f6',
          default_theme_accent_2: data.default_theme_accent_2 || '#6366f1',
          using_default_theme_colors: data.using_default_theme_colors !== false,
          license_preset_name: data.license_preset_name || 'CUSTOM',
          features: data.features || { customLogo: true, rentmanApi: true, customDomain: false, customColors: false },
        });
        setApiKeyMasked(Boolean(data.api_key_configured));
        setEventworxTokenMasked(Boolean(data.eventworx_api_token_configured));setLoaded(true);
      })
      .catch(error => setLoadError(error.message));
  }, [reload]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateApiKey = async () => {
    setApiKeyCheck({ status: 'loading', message: 'Prüfe Rentman API-Key...' });
    try {
      const response = await apiFetch(`${API_BASE}/settings/validate-rentman-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: settings.api_key }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Rentman API-Key konnte nicht geprüft werden.');
      }
      setApiKeyCheck({ status: 'success', message: data.message || 'API-Key ist gültig.' });
    } catch (error) {
      setApiKeyCheck({ status: 'error', message: error.message });
    }
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    const shouldResetThemeColors = settings.features?.customColors && settings.using_default_theme_colors;
    const response = await apiFetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...settings,
        api_key_masked: apiKeyMasked,
        eventworx_api_token_masked: eventworxTokenMasked,
        reset_theme_colors: shouldResetThemeColors,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if(!response.ok)throw new Error(data.error || 'Einstellungen konnten nicht gespeichert werden.');
    const nextSettings = data.settings ? {
      ...settings,
      ...data.settings,
      features: data.settings.features || settings.features,
    } : settings;
    setSettings(nextSettings);
    setApiKeyMasked(Boolean(nextSettings.api_key_configured && nextSettings.api_key));
    window.dispatchEvent(new CustomEvent('dispoboard:settings-updated', { detail: nextSettings }));
    setDirty(false);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  const resetThemeColors = async () => {
    const response = await apiFetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...settings,
          api_key_masked: apiKeyMasked,
        eventworx_api_token_masked: eventworxTokenMasked,
          reset_theme_colors: true,
        }),
      });
    const data = await response.json().catch(() => ({}));
    if(!response.ok)throw new Error(data.error || 'Einstellungen konnten nicht gespeichert werden.');
    const nextSettings = data.settings ? {
      ...settings,
      ...data.settings,
      features: data.settings.features || settings.features,
    } : {
      ...settings,
      theme_accent_1: settings.default_theme_accent_1 || settings.theme_accent_1,
      theme_accent_2: settings.default_theme_accent_2 || settings.theme_accent_2,
      using_default_theme_colors: true,
    };
    setSettings(nextSettings);
    setApiKeyMasked(Boolean(nextSettings.api_key_configured && nextSettings.api_key));
    window.dispatchEvent(new CustomEvent('dispoboard:settings-updated', { detail: nextSettings }));
    setDirty(false);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  const triggerProxySync = async () => {
    setProxySyncMessage('');
    const response = await apiFetch(`${API_BASE}/settings/proxy-sync`, { method: 'POST' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Proxy-Sync fehlgeschlagen');
    }
    setProxySyncMessage('Proxy-Prüfung gestartet. Falls der DNS-Eintrag vorhanden ist, wird die Domain aktualisiert.');
  };

  const isMobile = viewportWidth < 960;
  const gridStyle = viewportWidth < 760 ? { ...styles.grid, gridTemplateColumns: '1fr' } : styles.grid;

  if(loadError)return <EmptyState action={<Button onClick={()=>setReload(x=>x+1)}>Erneut laden</Button>}>{loadError}</EmptyState>;
  if(!loaded)return <Skeleton/>;
  return (
    <form onChangeCapture={()=>setDirty(true)} className="ws-settings" onSubmit={async event=>{event.preventDefault();if(saving)return;setSaving(true);setSaveError('');try{await saveSettings(event)}catch(error){setSaveError(error.message)}finally{setSaving(false)}}}><PageHeader title="Instanzeinstellungen" description="Branding, Integrationen und Domain für dieses Board."/><Tabs label="Einstellungsbereich" value={section} onChange={setSection} items={[{value:'branding',label:'Branding'},{value:'integrations',label:'Integrationen'},{value:'domain',label:'Domain'}]}/>{saveError&&<p role="alert">{saveError}</p>}
      <div style={mergeStyles(styles.layout, isMobile && { gridTemplateColumns: '1fr' })}>
        <div style={styles.stack}>
          <section style={styles.card}>


            <div style={{...styles.section,display:section==='branding'?'grid':'none'}}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Branding</h3>
                <p style={styles.sectionText}>Name und Logo werden direkt in Sidebar, Login und Browserbranding verwendet.</p>
              </div>
              <div style={gridStyle}>
                <label style={styles.field}>
                  <span style={styles.label}>Firmenname</span>
                  <input
                    style={styles.input}
                    value={settings.board_name}
                    onChange={(event) => setSettings({ ...settings, board_name: event.target.value })}
                    placeholder="Firmenname"
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Seitenlogo URL</span>
                  <input
                    style={styles.input}
                    value={settings.site_logo_url}
                    onChange={(event) => setSettings({ ...settings, site_logo_url: event.target.value })}
                    placeholder="https://..."
                    disabled={!settings.features?.customLogo}
                  />
                  {!settings.features?.customLogo && <p style={styles.hint}>Custom Logo ist in deiner Lizenz nicht freigeschaltet.</p>}
                </label>
              </div>
            </div>

            <div style={{...styles.section,display:section==='integrations'?'grid':'none'}}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Rentman API</h3>
                <p style={styles.sectionText}>Der API-Key wird für Datenabgleich und Lager Lookup genutzt.</p>
              </div>
              <div style={gridStyle}>
                <label style={styles.field}>
                  <span style={styles.label}>Rentman API-Key</span>
                  <input
                    style={styles.input}
                    type="password"
                    value={settings.api_key}
                    onChange={(event) => {
                      setApiKeyMasked(false);
                      setSettings({ ...settings, api_key: event.target.value });
                      setApiKeyCheck({ status: 'idle', message: '' });
                    }}
                    disabled={!settings.features?.rentmanApi}
                  />
                  {apiKeyMasked && settings.api_key ? (
                    <p style={styles.hint}>Gespeicherter API-Key ist maskiert. Tippe einen neuen Wert ein, um ihn zu ersetzen.</p>
                  ) : null}
                  {!settings.features?.rentmanApi && <p style={styles.hint}>Rentman API ist in deiner Lizenz nicht freigeschaltet.</p>}
                </label>
              </div>
              {settings.features?.rentmanApi && (
                <div style={styles.buttonRow}>
                  <button
                    style={styles.mutedButton}
                    type="button"
                    onClick={() => validateApiKey().catch(() => {})}
                    disabled={!settings.api_key.trim() || apiKeyCheck.status === 'loading' || apiKeyMasked}
                  >
                    {apiKeyCheck.status === 'loading' ? 'Prüft...' : 'API-Key prüfen'}
                  </button>
                  {apiKeyCheck.message && (
                    <p style={apiKeyCheck.status === 'success' ? styles.statusSuccess : styles.statusError}>
                      {apiKeyCheck.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div style={{...styles.section,display:section==='integrations'?'grid':'none'}}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Eventworx API</h3>
                <p style={styles.sectionText}>Serverseitiger Auftragsimport. Das S-API-TOKEN wird nicht an Displays oder Browser-APIs ausgeliefert.</p>
              </div>
              <div style={gridStyle}>
                <label style={styles.field}><span style={styles.label}>Mandanten-Basis-URL</span><input style={styles.input} value={settings.eventworx_base_url} onChange={(event) => setSettings({ ...settings, eventworx_base_url: event.target.value })} placeholder="https://mandant.eventworx.biz" /></label>
                <label style={styles.field}><span style={styles.label}>S-API-TOKEN</span><input style={styles.input} type="password" value={settings.eventworx_api_token} onChange={(event) => { setEventworxTokenMasked(false); setSettings({ ...settings, eventworx_api_token: event.target.value }); }} />{eventworxTokenMasked && <p style={styles.hint}>Gespeichertes Token ist maskiert.</p>}</label>
              </div>
            </div>

            <div style={{...styles.section,display:section==='domain'?'grid':'none'}}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Eigene Domain</h3>
                <p style={styles.sectionText}>Optional kann die Instanz auf eine eigene Subdomain geroutet werden.</p>
              </div>
              <div style={gridStyle}>
                <label style={styles.field}>
                  <span style={styles.label}>Eigene Domain</span>
                  <div style={styles.domainInputRow}>
                    <input
                      style={styles.input}
                      value={settings.custom_domain}
                      onChange={(event) => setSettings({ ...settings, custom_domain: event.target.value })}
                      placeholder="dispo.deine-domain.de"
                      disabled={!settings.features?.customDomain}
                    />
                    <button
                      style={styles.domainSaveButton}
                      type="submit"
                      disabled={!settings.features?.customDomain}
                      title="Domain speichern"
                      aria-label="Domain speichern"
                    >
                      ✓
                    </button>
                  </div>
                  {settings.features?.customDomain ? (
                    <p style={styles.hint}>Lege einen CNAME von deiner Wunsch-Adresse auf {customDomainTarget} an und prüfe die Proxy-Konfiguration anschließend erneut.</p>
                  ) : (
                    <p style={styles.hint}>Eigene Domains sind in deiner Lizenz nicht freigeschaltet.</p>
                  )}
                </label>
              </div>
              {settings.features?.customDomain && (
                <div style={styles.buttonRow}>
                  <button
                    style={styles.button}
                    type="button"
                    onClick={() => triggerProxySync().catch((error) => setProxySyncMessage(error.message))}
                  >
                    Proxy prüfen
                  </button>
                  {proxySyncMessage && <p style={styles.hint}>{proxySyncMessage}</p>}
                </div>
              )}
            </div>

            <div style={{...styles.section,display:section==='branding'?'grid':'none'}}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Designfarben</h3>
                <p style={styles.sectionText}>Die Farben werden auf Sidebar, Buttons und Akzente der gesamten Board-Instanz angewendet.</p>
              </div>
              <div style={gridStyle}>
                <label style={styles.field}>
                  <span style={styles.label}>Akzentfarbe 1</span>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={settings.theme_accent_1 || '#3b82f6'}
                    onChange={(event) => setSettings({ ...settings, theme_accent_1: event.target.value, using_default_theme_colors: false })}
                    disabled={!settings.features?.customColors}
                  />
                  {!settings.features?.customColors && <p style={styles.hint}>Custom Farben sind in deiner Lizenz nicht freigeschaltet.</p>}
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Akzentfarbe 2</span>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={settings.theme_accent_2 || '#6366f1'}
                    onChange={(event) => setSettings({ ...settings, theme_accent_2: event.target.value, using_default_theme_colors: false })}
                    disabled={!settings.features?.customColors}
                  />
                  {settings.features?.customColors && (
                    <p style={styles.hint}>
                      {settings.using_default_theme_colors
                        ? 'Aktuell werden die Management-Standardfarben verwendet.'
                        : 'Aktuell werden eigene Custom-Farben verwendet.'}
                    </p>
                  )}
                </label>
              </div>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.button} type="submit" disabled={saving}>{saving?'Speichert …':'Speichern'}</button>
              <button
                style={styles.mutedButton}
                type="button"
                disabled={!settings.features?.customColors}
                onClick={() => resetThemeColors().catch(error => setSaveError(error.message))}
              >
                Farben auf Standard reset
              </button>
              {saved && <p style={styles.statusSuccess}>Gespeichert</p>}
            </div>
          </section>
        </div>

        <aside style={styles.stack}>
          <section style={styles.card}>
            <h3 style={styles.sideCardTitle}>Instanz Vorschau</h3>
            <p style={styles.sideCardText}>So wirken Name und Logo im neuen Board-Rahmen.</p>
            <div style={{ marginTop: '16px', display: 'grid', gap: '14px' }}>
              <div style={styles.previewFrame}>
                {settings.site_logo_url ? (
                  <img style={styles.previewImage} src={settings.site_logo_url} alt="Logo" />
                ) : (
                  <div style={styles.previewFallback}>DB</div>
                )}
              </div>
              <div style={styles.licenseBox}>
                <span style={styles.label}>Board Name</span>
                <strong style={styles.licenseValue}>{settings.board_name || 'DispoBoard'}</strong>
              </div>
            </div>
          </section>

          <section style={styles.card}>
            <h3 style={styles.sideCardTitle}>Lizenz</h3>
            <p style={styles.sideCardText}>Aktives Lizenzprofil für diese Instanz.</p>
            <div style={{ marginTop: '16px' }} />
            <div style={styles.licenseBox}>
              <span style={styles.label}>Profil</span>
              <strong style={styles.licenseValue}>{settings.license_preset_name || 'CUSTOM'}</strong>
            </div>
          </section>

          <section style={styles.card}>
            <h3 style={styles.sideCardTitle}>Aktive Farben</h3>
            <p style={styles.sideCardText}>Die folgenden Akzente werden aktuell im Board genutzt.</p>
            <div style={{ marginTop: '16px', display: 'grid', gap: '16px' }}>
              <div style={styles.colorRow}>
                <div style={{ ...styles.colorSwatch, background: settings.theme_accent_1 || '#3b82f6' }} />
                <div style={{ ...styles.colorSwatch, background: settings.theme_accent_2 || '#6366f1' }} />
              </div>
              <div style={styles.domainRow}>
                <div style={{ display: 'grid', gap: '4px', minWidth: 0 }}>
                  <span style={styles.label}>Domain Ziel</span>
                  <strong style={{ color: 'var(--board-text-strong, #fff)', overflowWrap: 'anywhere' }}>{customDomainTarget}</strong>
                </div>
                <span style={styles.domainStatus}>Verfügbar</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </form>
  );
}

export default GeneralSettings;
